import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  UncontrolledTooltip,
  Button,
} from "reactstrap";

import Select from "react-select";

// alerta
import SweetAlert from "react-bootstrap-sweetalert";
// componentes

import ColorPicker from "../../../components/ColorPicker/ColorPicker";
import ButtonSpinner from "../../../components/Buttons/ButtonSpinner";
import ConfigEventLinks from "./ConfigEventLinks";

import {
  create,
  edit,
  getByContractAndEvent,
} from "../../../utils/services/portalEvents/portalEventsConfigEvents";
import { getButtons } from "../../../utils/services/portalEvents/portalEventsButtonsMenu";
import { getGroups } from "../../../utils/services/group";
import { getEvents } from "../../../utils/services/events";
import { uploadFiles } from "../../../utils/services/files";

const SAVE_ERROR_MESSAGE =
  "Não foi possível salvar as configurações do evento.";

// 413 = payload acima do limite do servidor. No fluxo desta tela a imagem é o
// único dado que chega perto desse limite, então a mensagem aponta para ela.
function resolveErrorMessage(error) {
  if (error?.response?.status === 413) {
    return "A imagem do evento excede o tamanho máximo aceito pelo servidor. Reduza o arquivo (resolução ou qualidade) e tente novamente.";
  }

  if (error?.message === "INVALID_IMAGE") {
    return "Selecione um arquivo de imagem válido para o evento.";
  }

  if (error?.message === "UPLOAD_FAILED") {
    return "Não foi possível enviar a imagem do evento. Verifique o arquivo e tente novamente.";
  }

  if (error?.response?.data?.message === "REGISTRATION_EVENT_REQUIRED") {
    return "Selecione o evento que, ao receber o cadastro do participante, esconde esta configuração.";
  }

  if (error?.response?.data?.message === "REGISTRATION_EVENT_NOT_IN_CONTRACT") {
    return "O evento selecionado não pertence a este contrato. Escolha outro evento.";
  }

  if (error?.response?.data?.message === "VALIDATION_ERROR") {
    return "Alguns campos estão inválidos. Verifique os dados do formulário, inclusive os links, e tente novamente.";
  }

  return SAVE_ERROR_MESSAGE;
}

export default function ModalConfigEventPortal(props) {
  const { isOpen, toggle, eventId, contractId } = props;

  // form
  const [isVisible, setIsVisible] = useState(false);

  const [typeRequest, setTypeRequest] = useState("post");
  const [imageEvent, setImageEvent] = useState("");
  const [imageEventPreview, setImageEventPreview] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mainScreen, setMainScreen] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#FFFF");
  const [secondaryColor, setSecondaryColor] = useState("#FFFF");
  const [buttons, setButtons] = useState([]);
  const [buttonSelected, setButtonSelected] = useState(null);
  const [links, setLinks] = useState([]);
  const [groups, setGroups] = useState([]);
  // visibilidade por cadastro: visível enquanto o participante não estiver
  // cadastrado no evento selecionado. Independente da visibilidade por período.
  const [isVisibleUntilRegistration, setIsVisibleUntilRegistration] =
    useState(false);
  const [registrationEventId, setRegistrationEventId] = useState(null);
  const [contractEvents, setContractEvents] = useState([]);

  // alerta
  const [success, setSuccess] = useState(false);
  const [errorAlert, setErrorAlert] = useState(null);
  // carregando
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // erros
  const [errors, setErrors] = useState({});

  const onImageChange = (event, setStateFunction) => {
    if (event.target.files && event.target.files[0]) {
      let reader = new FileReader();
      reader.onload = (e) => {
        setStateFunction(e.target.result);
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  async function getImage(photo) {
    if (!photo?.type?.startsWith("image/")) {
      throw new Error("INVALID_IMAGE");
    }

    const data = new FormData();

    data.append("files", photo);
    data.append("folder", "configeventoportal");
    data.append("eventId", eventId);

    const response = await uploadFiles(data);

    if (!Array.isArray(response) || !response[0]?.url) {
      throw new Error("UPLOAD_FAILED");
    }

    return response[0].url;
  }
  const handleSaveConfig = async () => {
    setLoading(true);
    setErrors({});

    let newErrors = {};

    if (!imageEvent) {
      newErrors.imageEvent = "O campo Imagem da Tela de Login é obrigatório";
    }

    if (isVisible) {
      if (!startDate) {
        newErrors.dateStart = "O campo Data e Hora de Início é obrigatório";
      }
      if (!endDate) {
        newErrors.dateEnd = "O campo Data e Hora de Fim é obrigatório";
      }

      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        newErrors.dateEnd = "A data de fim deve ser maior que a data de início";
      }
    }

    if (isVisibleUntilRegistration && !registrationEventId) {
      newErrors.registrationEvent = "Selecione o evento que esconde a configuração após o cadastro";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    await submitForm();
  };

  const handleButton = (button) => {
    if (!button) {
      setButtonSelected(button);
      setMainScreen("");
    } else {
      setButtonSelected(button);
      setMainScreen(button.value);
    }
  };
  const formatDateForInput = (isoString) => {
    if (!isoString) return null;
    if (isoString) {
      return isoString.slice(0, 16);
    }
  };

  // "outro evento": o próprio evento fica fora da lista.
  const eventOptions = contractEvents
    .filter((event) => event.id !== eventId)
    .map((event) => ({ label: event.title, value: event.id }));

  // A config e a lista de eventos carregam em paralelo, e um evento pode ter
  // sido desativado depois — sem opção correspondente, mostra o id gravado.
  const registrationEventOption = registrationEventId
    ? eventOptions.find((option) => option.value === registrationEventId) || {
        label: registrationEventId,
        value: registrationEventId,
      }
    : null;

  const formatDateToSave = (inputValue) => {
    if (!inputValue) return "";
    return `${inputValue}:00.000Z`;
  };

  async function submitForm() {
    setLoading(true);

    try {
      const imageChanged =
        typeRequest === "post" || imageEvent !== imageEventPreview;

      const imageEvento = imageChanged
        ? await getImage(imageEvent)
        : imageEvent;

      const data = {
        contractId,
        eventId,
        imageEvent: imageEvento,
        primaryColor,
        secondaryColor,
        isVisible,
        mainScreen,
        links,
        isVisibleUntilRegistration,
        registrationEventId: isVisibleUntilRegistration
          ? registrationEventId
          : null,
        startDate: isVisible ? formatDateToSave(startDate) : null,
        endDate: isVisible ? formatDateToSave(endDate) : null,
      };

      let res = "";

      if (typeRequest === "post") {
        res = await create(data);
      }
      if (typeRequest === "put") {
        delete data.contractId;
        delete data.eventId;
        res = await edit(data, contractId, eventId);
      }

      if (res?.message === "created" || res?.message === "updated") {
        setSuccess(true);
      } else {
        setErrorAlert(SAVE_ERROR_MESSAGE);
      }
    } catch (error) {
      setErrorAlert(resolveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function getConfig() {
    getByContractAndEvent(contractId, eventId)
      .then((res) => {
        if (res?.message === "findByContractAndEvent" && res?.data) {
          const data = res?.data;

          setTypeRequest("put");
          setIsVisible(data?.isVisible);
          setImageEvent(data?.imageEvent);
          setImageEventPreview(data?.imageEvent);
          setPrimaryColor(data?.primaryColor);
          setSecondaryColor(data?.secondaryColor);
          setStartDate(
            data?.startDate ? formatDateForInput(data?.startDate) : null
          );
          setEndDate(data?.endDate ? formatDateForInput(data?.endDate) : null);
          setMainScreen(data?.mainScreen);
          setLinks(Array.isArray(data?.links) ? data.links : []);
          setIsVisibleUntilRegistration(!!data?.isVisibleUntilRegistration);
          setRegistrationEventId(data?.registrationEventId);

          getButtonsList(data?.mainScreen);
        } else {
          getButtonsList(null);
        }
      })
      .catch(() => {
        setErrorAlert("Não foi possível carregar as configurações do evento.");
      })
      .finally(() => setLoadingConfig(false));
  }

  // A API filtra os eventos pelo contrato do admin logado — o mesmo contrato
  // da configuração —, então a lista já vem restrita ao contrato.
  async function getContractEvents() {
    getEvents({
      offset: 1,
      limit: 1000,
      search: "",
      columnSearch: "",
      activate: true,
    })
      .then((res) => {
        setContractEvents(res?.findAllEventsData?.data || []);
      })
      .catch(() => setContractEvents([]));
  }

  async function getGroupsList() {
    getGroups({ eventId, offset: 1, limit: 0, search: "" })
      .then((res) => {
        if (res?.message === "Groups_by_event" && res?.findOneGroupData?.count > 0) {
          setGroups(res.findOneGroupData.data);
        } else setGroups([]);
      })
      .catch(() => setGroups([]));
  }

  async function getButtonsList(mainScreen) {
    getButtons(eventId).then((res) => {
      if (res?.message === "BUTTONS_LIST" && res?.listButtons?.data) {
        setButtons(res?.listButtons?.data);

        if (mainScreen) {
          const buttonSel = res?.listButtons?.data.filter(
            (f) => f.type === mainScreen
          );

          if (buttonSel?.length > 0) {
            setButtonSelected({
              label: buttonSel[0].name,
              value: buttonSel[0].type,
            });
          }
        }
      }
    });
  }

  useEffect(() => {
    getConfig();
    getGroupsList();
    getContractEvents();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      {success && (
        <SweetAlert
          title="Sucesso"
          success
          onConfirm={() => {
            toggle();
            setSuccess(false);
          }}
          onCancel={() => setSuccess(false)}
        >
          Configurações salvas com sucesso!
        </SweetAlert>
      )}
      {errorAlert && (
        <SweetAlert
          title="Erro"
          danger
          onConfirm={() => setErrorAlert(null)}
          onCancel={() => setErrorAlert(null)}
        >
          {errorAlert}
        </SweetAlert>
      )}
      <Modal isOpen={isOpen} centered>
        <ModalHeader toggle={toggle}>Configurar Evento</ModalHeader>
        <ModalBody>
          {loadingConfig ? (
            <div style={{ width: "100%", textAlign: "center" }}>
              <a>Carregando...</a>
            </div>
          ) : (
            <div>
              <Row>
                <Col lg={typeRequest === "put" ? 8 : 12}>
                  <label
                    htmlFor="formFile"
                    className="form-label"
                    style={{ display: "flex" }}
                  >
                    Imagem do Evento*{" "}
                    <div
                      id="tooltipImageEvent"
                      style={{ paddingRight: "7px", height: "16px" }}
                    >
                      <div className="show-tips">
                        <span>?</span>
                      </div>
                      <UncontrolledTooltip
                        style={{ backgroundColor: "#6f42c1" }}
                        placement="right"
                        target="tooltipImageEvent"
                      >
                        Tamanho: 1020px x 780px
                      </UncontrolledTooltip>
                    </div>
                  </label>
                  <input
                    className="form-control"
                    type="file"
                    id="formFile"
                    onChange={(e) => {
                      onImageChange(e, setImageEventPreview);
                      setImageEvent(e.target?.files[0]);
                    }}
                  />
                  {errors && errors.imageEvent && (
                    <a style={{ color: "red" }}>{errors.imageEvent}</a>
                  )}
                </Col>
                {typeRequest === "put" && imageEventPreview && (
                  <Col
                    sm={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <>
                      <img
                        alt="edit"
                        src={imageEventPreview}
                        style={{ width: 50 }}
                      />
                    </>
                  </Col>
                )}
              </Row>

              <Row>
                <Col md={6}>
                  <ColorPicker
                    classOfContainer="colorPrimary"
                    labelText="Cor Primária*"
                    setStateFunc={setPrimaryColor}
                    defaultColor={primaryColor}
                    idDiv={"colorPrimary"}
                    tip={"Cor do card e do menu interno do evento"}
                  />
                </Col>

                <Col md={6}>
                  <ColorPicker
                    classOfContainer="colorSecondary"
                    labelText="Cor Secundária*"
                    setStateFunc={setSecondaryColor}
                    defaultColor={secondaryColor}
                    idDiv={"colorSecondary"}
                    tip={"Cor dos detalhes do evento"}
                  />
                </Col>
              </Row>

              <Row style={{ marginTop: 20 }}>
                <Col md={12}>
                  <input
                    className="input-mg-rgt"
                    id="checkVisible"
                    type="checkbox"
                    onChange={(e) => setIsVisible(e.target.checked)}
                    checked={isVisible}
                  />
                  <label className="form-label" htmlFor="checkVisible">
                    Visível
                  </label>
                </Col>
              </Row>

              {isVisible && (
                <>
                  <Row>
                    <Col md={6} style={{ marginTop: 10 }}>
                      <label className="form-label">Data e Hora Inicio*</label>
                      <input
                        className="form-control"
                        type="datetime-local"
                        name="startDateTime"
                        value={formatDateForInput(startDate)}
                        onChange={(e) => setStartDate(e.target.value)}
                        max={endDate}
                      />
                      {errors && errors.dateStart && (
                        <a style={{ color: "red" }}>{errors.dateStart}</a>
                      )}
                    </Col>
                    <Col md={6} style={{ marginTop: 10 }}>
                      <label className="form-label">Data e Hora Fim*</label>
                      <input
                        className="form-control"
                        type="datetime-local"
                        name="endDateTime"
                        value={formatDateForInput(endDate)}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                      />
                      {errors && errors.dateEnd && (
                        <a style={{ color: "red" }}>{errors.dateEnd}</a>
                      )}
                    </Col>
                  </Row>
                </>
              )}

              <Row style={{ marginTop: 20 }}>
                <Col md={12}>
                  <input
                    className="input-mg-rgt"
                    id="checkVisibleUntilRegistration"
                    type="checkbox"
                    onChange={(e) =>
                      setIsVisibleUntilRegistration(e.target.checked)
                    }
                    checked={isVisibleUntilRegistration}
                  />
                  <label
                    className="form-label"
                    htmlFor="checkVisibleUntilRegistration"
                    style={{ display: "inline-flex" }}
                  >
                    Visível enquanto não cadastrado em outro evento{" "}
                    <div
                      id="tooltipVisibleUntilRegistration"
                      style={{ paddingRight: "7px", height: "16px" }}
                    >
                      <div className="show-tips">
                        <span>?</span>
                      </div>
                      <UncontrolledTooltip
                        style={{ backgroundColor: "#6f42c1" }}
                        placement="right"
                        target="tooltipVisibleUntilRegistration"
                      >
                        Quando o participante se cadastrar no evento
                        selecionado, esta configuração deixa de ser exibida.
                        Funciona junto com a visibilidade por período.
                      </UncontrolledTooltip>
                    </div>
                  </label>
                </Col>
              </Row>

              {isVisibleUntilRegistration && (
                <Row style={{ marginTop: 10 }}>
                  <Col md={12}>
                    <label className="form-label">Evento do cadastro*</label>
                    <Select
                      value={registrationEventOption}
                      placeholder={"Selecione"}
                      isClearable={true}
                      isMulti={false}
                      onChange={(option) =>
                        setRegistrationEventId(option ? option.value : null)
                      }
                      noOptionsMessage={() => "Nenhuma opção encontrada"}
                      options={eventOptions}
                      classNamePrefix="select2-selection"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (provided) => ({ ...provided, maxHeight: 150 }),
                        menuList: (provided) => ({
                          ...provided,
                          maxHeight: 150,
                          overflowY: "auto",
                        }),
                      }}
                    />
                    {errors && errors.registrationEvent && (
                      <a style={{ color: "red" }}>{errors.registrationEvent}</a>
                    )}
                  </Col>
                </Row>
              )}

              <Row style={{ marginBottom: 10, marginTop: "10px" }}>
                <Col lg={12}>
                  <div>
                    <label className="form-label">Tela Principal</label>
                    <Select
                      value={buttonSelected}
                      placeholder={"Selecione"}
                      isClearable={true}
                      isMulti={false}
                      onChange={(e) => {
                        handleButton(e);
                      }}
                      noOptionsMessage={() => "Nenhuma opção encontrada"}
                      options={[
                        {
                          options:
                            buttons?.length > 0
                              ? [
                                  ...buttons?.map((e) => {
                                    return { label: e.name, value: e.type };
                                  }),
                                ]
                              : [],
                        },
                      ]}
                      classNamePrefix="select2-selection"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          maxHeight: 150,
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          maxHeight: 150,
                          overflowY: "auto",
                        }),
                      }}
                    />
                  </div>
                </Col>
              </Row>

              <ConfigEventLinks
                links={links}
                setLinks={setLinks}
                groups={groups}
              />

              <Row>
                <Col
                  md={12}
                  style={{
                    width: "100%",
                    margin: "20px auto 0",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    color="danger"
                    className="btn waves-effect waves-light"
                    style={{ width: 80 }}
                    onClick={() => toggle()}
                  >
                    Fechar
                  </Button>
                  <ButtonSpinner
                    title={"Salvar"}
                    marginLeft={10}
                    onClick={handleSaveConfig}
                    isLoading={loading}
                  />
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
